import { convertMbToBytes } from '@/lib/formatters/number.formatter'

export type ValidExtensions = 'csv' | 'xls' | 'xlsx' | 'pdf' | 'txt' | 'jpeg' | 'png' | 'jpg'

// Serializable projection of a File, so an upload budget can be tracked in a store without
// holding the File objects themselves
export type AttachedFile = {
  name: string
  type: string
  size: number
}

// Files attached across several fields, keyed per field, sharing a single size budget
export type FilesRecord = Record<string, AttachedFile[]>

// Fallback budget when no shared limit is set, in Mb
const DEFAULT_UPLOAD_LIMIT_MB = 10

export function toAttachedFile(file: File): AttachedFile {
  return { name: file.name, type: file.type, size: file.size }
}

export function sumFileSizes(files: { size: number }[]): number {
  return files.reduce((total, file) => total + file.size, 0)
}

export function sumRecordFileSizes(record: FilesRecord): number {
  return sumFileSizes(Object.values(record).flat())
}

// Bytes still available in the budget; falls back to the default limit when none is shared
export function getRemainingSize({
  sharedLimitMb,
  usedBytes,
}: {
  sharedLimitMb?: number
  usedBytes: number
}): number {
  if (sharedLimitMb === undefined) return convertMbToBytes(DEFAULT_UPLOAD_LIMIT_MB)

  return convertMbToBytes(sharedLimitMb) - usedBytes
}

export function fitsInRemainingSize(files: { size: number }[], remainingSize: number): boolean {
  return sumFileSizes(files) <= remainingSize
}

type AcceptedMimeTypes =
  | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  | 'application/vnd.ms-excel'
  | 'text/csv'
  | 'image/jpeg'
  | 'image/png'
  | 'application/pdf'

export function extensionToMime(extension: ValidExtensions): AcceptedMimeTypes | '' {
  const extensionMap: Record<string, AcceptedMimeTypes> = {
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    csv: 'text/csv',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    pdf: 'application/pdf',
  }

  return extensionMap[extension] || ''
}

export function mimeToExtension(mime: AcceptedMimeTypes | string): ValidExtensions | '' {
  const mimeMap: Record<AcceptedMimeTypes | string, ValidExtensions> = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'text/csv': 'csv',
    'image/jpeg': 'jpeg',
    'image/png': 'png',
    'application/pdf': 'pdf',
  }

  return mimeMap[mime] || ''
}

// Files whose extension is not part of the allowed list, resolved from their mime type.
// A mime type outside `mimeToExtension`'s map resolves to '', which is never in
// `validExtensions` either, so it correctly counts as invalid rather than slipping through
export function getInvalidExtensionFiles(
  files: File[],
  validExtensions: ValidExtensions[],
): File[] {
  return files.filter((file) => {
    const extension = mimeToExtension(file.type)

    return !validExtensions.includes(extension as ValidExtensions)
  })
}
